import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  contactEndpoints,
  operationsClient,
  publicContactPaymentMethodRequestEndpoints,
} from '@/lib/api';
import { billingV2Post, isIdempotencyRequestInProgress } from '@/lib/api/clients/billing-v2';
import { ApiError, isApiError, normalizeError } from '@/lib/api/errors';
import { isAcceptedBankDebit } from '@/lib/payments/savedPaymentMethods';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { publicGet, publicPost } from '@/lib/api/publicClient';
import { contactKeys, paymentKeys } from '@/lib/queryKeys';
import type { CollectReceiptDelivery } from '@/hooks/receipts/types';
import { collectPaymentToast, showReceiptToast } from '@/hooks/receipts/receipt-delivery-toast';
import type {
  CompletePublicTokenizeRequest,
  CollectContactPaymentDto,
  CompletePublicPaymentMethodRequest,
  ContactPaymentLinkResult,
  ContactPaymentMethod,
  ContactPaymentMethodRequestDetails,
  ContactPaymentMethodSetupRail,
  ContactPaymentMethodSetupIntent,
  ContactPaymentRequestResult,
  CreateContactPaymentLinkDto,
  CreateContactPaymentMethodDto,
  PublicContactPaymentMethodSetupIntent,
  PublicSavedPaymentMethod,
  RecordContactPaymentDto,
  RequestContactPaymentMethodDto,
  TokenizeContactPaymentMethodDto,
  UpdateContactPaymentMethodDto,
} from '@/types/contact';
import { normalizeContactPaymentMethod } from './useContacts';
import { getContactPaymentRequestDeliveryNotice } from '@/lib/contacts/paymentRequestDelivery';

function invalidateContactPaymentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  contactId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: contactKeys.paymentMethods(contactId),
  });
  void queryClient.invalidateQueries({
    queryKey: contactKeys.paymentsBase(contactId),
  });
  void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: contactKeys.invoicesBase(contactId) });
  void queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
}

interface ContactPaymentMethodsOptions {
  enabled?: boolean;
}

export function useContactPaymentMethods(
  contactId: string,
  options: ContactPaymentMethodsOptions = {},
) {
  return useApiQuery<ContactPaymentMethod[]>({
    queryKey: contactKeys.paymentMethods(contactId),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>[]>(
        operationsClient,
        contactEndpoints.paymentMethods(contactId),
      );
      return raw
        .map(normalizeContactPaymentMethod)
        .filter((method): method is ContactPaymentMethod => method !== null);
    },
    enabled: Boolean(contactId) && (options.enabled ?? true),
    showErrorToast: false,
  });
}

/**
 * Every figure here is optional because field masks remove it: PRICING and
 * PAYMENT_INFO withhold the money, CUSTOMER_FINANCIALS the whole summary. A
 * consumer has to handle a missing value rather than format `undefined`.
 */
export interface ContactPaymentBalance {
  summary: {
    totalOutstanding?: number;
    unappliedAmount?: number;
    openInvoiceCount?: number;
  };
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    balanceDue?: number;
    dueDate?: string | null;
    status: string;
  }>;
  // Payable invoices only, already in the exact order the backend auto-apply
  // sweep consumes them. The Record Payment preview iterates this verbatim so it
  // can never disagree with where the server actually applies the payment.
  openInvoices: Array<{
    id: string;
    invoiceNumber: string;
    balanceDue?: number;
  }>;
}

/**
 * The contact's outstanding balance + open invoices, used to preview where an
 * "apply to open invoices automatically" payment will land (oldest-first) before
 * the operator confirms it.
 */
export function useContactPaymentBalance(
  contactId: string,
  options: ContactPaymentMethodsOptions = {},
) {
  return useApiQuery<ContactPaymentBalance>({
    queryKey: [...contactKeys.paymentsBase(contactId), 'balance'],
    queryFn: () =>
      apiGet<ContactPaymentBalance>(operationsClient, `/payments/balance/contact/${contactId}`),
    enabled: Boolean(contactId) && (options.enabled ?? true),
    showErrorToast: false,
  });
}

export function useCreateContactPaymentMethod(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactPaymentMethod, CreateContactPaymentMethodDto>({
    mutationFn: async (dto) => {
      const raw = await apiPost<Record<string, unknown>>(
        operationsClient,
        contactEndpoints.paymentMethods(contactId),
        dto,
      );
      const normalized = normalizeContactPaymentMethod(raw);
      if (!normalized) {
        throw new Error('Stored payment method response was invalid');
      }
      return normalized;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<ContactPaymentMethod[]>(
        contactKeys.paymentMethods(contactId),
        (current = []) =>
          current.some((method) => method.id === created.id) ? current : [...current, created],
      );
      invalidateContactPaymentQueries(queryClient, contactId);
      toast.success('Payment method added');
    },
  });
}

export function useUpdateContactPaymentMethod(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<
    ContactPaymentMethod,
    { paymentMethodId: string; dto: UpdateContactPaymentMethodDto }
  >({
    mutationFn: async ({ paymentMethodId, dto }) => {
      const raw = await apiPatch<Record<string, unknown>>(
        operationsClient,
        contactEndpoints.paymentMethod(contactId, paymentMethodId),
        dto,
      );
      const normalized = normalizeContactPaymentMethod(raw);
      if (!normalized) {
        throw new Error('Updated payment method response was invalid');
      }
      return normalized;
    },
    onSuccess: () => {
      invalidateContactPaymentQueries(queryClient, contactId);
      toast.success('Payment method updated');
    },
  });
}

export function useDeleteContactPaymentMethod(
  contactId: string,
  {
    notFoundIsRemoved = false,
  }: {
    /**
     * Treat a 404 as "already removed" — resolved, no error toast. For the
     * new-method charge dialogs' clean-up of a one-time method after a
     * refused charge: the server already takes a hard-declined one-time
     * method off at charge time, so the dialog's own delete finds it gone.
     */
    notFoundIsRemoved?: boolean;
  } = {},
) {
  const queryClient = useQueryClient();

  return useApiMutation<{ deleted: true }, string>({
    mutationFn: async (paymentMethodId) => {
      try {
        return await apiDelete<{ deleted: true }>(
          operationsClient,
          contactEndpoints.paymentMethod(contactId, paymentMethodId),
        );
      } catch (error) {
        const apiError = isApiError(error) ? error : normalizeError(error);
        if (notFoundIsRemoved && apiError.status === 404) return { deleted: true };
        throw error;
      }
    },
    onSuccess: () => {
      invalidateContactPaymentQueries(queryClient, contactId);
      toast.success('Payment method removed');
    },
  });
}

/** Retries of a setup-intent create the server is still running under the same key. */
const SETUP_INTENT_IN_PROGRESS_RETRIES = 5;

export function useCreateContactPaymentMethodSetupIntent(contactId: string) {
  return useApiMutation<
    ContactPaymentMethodSetupIntent,
    { paymentMethodType: ContactPaymentMethodSetupRail }
  >({
    mutationFn: async ({ paymentMethodType }) =>
      billingV2Post<
        ContactPaymentMethodSetupIntent,
        { contactId: string; paymentMethodType: ContactPaymentMethodSetupRail }
      >(billingV2Endpoints.paymentMethodSetupIntent(), { contactId, paymentMethodType }),
    // `billingV2Post` keys a request by its body, so a second create for the
    // same contact + rail sent while the first is still running (a collector
    // remounted, a rail switched away and straight back, a dialog reopened)
    // reuses the first one's idempotency key and is answered 409 "already
    // processing". That is not a failure: once the first create answers, a
    // retry either replays it (same key) or — the first having released its
    // key — mints a fresh SetupIntent. Only that code is retried; every other
    // error surfaces at once.
    retry: (failureCount, error) =>
      isIdempotencyRequestInProgress(error) && failureCount < SETUP_INTENT_IN_PROGRESS_RETRIES,
    retryDelay: (failureCount) => Math.min(250 * 2 ** failureCount, 2000),
  });
}

/**
 * The Stripe SetupIntent a new-method collector renders Elements against,
 * created as a QUERY keyed by the caller's `requestKey` — one per collector
 * instance, rail and attempt — which is also the request's `Idempotency-Key`.
 *
 * Why not the mutation above: it was fired from a mount effect, and React
 * StrictMode replays mount effects. The replay sent an identical create that
 * shared the first's (body-derived) idempotency key, the server answered 409
 * "already processing", and because a mutation only reports its LATEST call,
 * the collector showed "Unable to start … setup" whenever that 409 landed
 * last. A query is deduplicated per key, survives StrictMode's replay, and a
 * fresh `requestKey` (a new collector, a new attempt) is a fresh intent.
 *
 * A 409 "already processing" for the SAME key (the query was dropped and
 * rebuilt while its create was still in flight) is retried: the same key,
 * asked again once the first create has answered, replays its intent.
 */
export function useContactPaymentMethodSetupIntent(
  contactId: string,
  rail: ContactPaymentMethodSetupRail,
  { requestKey, enabled }: { requestKey: string; enabled: boolean },
) {
  return useApiQuery<ContactPaymentMethodSetupIntent>({
    queryKey: contactKeys.paymentMethodSetupIntent(contactId, rail, requestKey),
    queryFn: () =>
      billingV2Post<
        ContactPaymentMethodSetupIntent,
        { contactId: string; paymentMethodType: ContactPaymentMethodSetupRail }
      >(
        billingV2Endpoints.paymentMethodSetupIntent(),
        { contactId, paymentMethodType: rail },
        { headers: { 'Idempotency-Key': requestKey } },
      ),
    enabled,
    // An intent is minted once per key and never "refreshed" — a refetch
    // would only replay the same answer, or, after the server's replay
    // window, mint a second intent under Elements already mounted on the first.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) =>
      isIdempotencyRequestInProgress(error) && failureCount < SETUP_INTENT_IN_PROGRESS_RETRIES,
    retryDelay: (failureCount) => Math.min(250 * 2 ** failureCount, 2000),
    // The collector renders the failure in place.
    showErrorToast: false,
  });
}

/**
 * Payabli save-a-card: exchange the embedded component's temporary token for a
 * permanent stored method (backend binds it to the contactId) and persist it.
 * The Payabli analog of the Stripe setup-intent + create flow above.
 */
export function useTokenizeContactPaymentMethod(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactPaymentMethod, TokenizeContactPaymentMethodDto>({
    mutationFn: async (dto) => {
      const raw = await apiPost<Record<string, unknown>>(
        operationsClient,
        contactEndpoints.paymentMethodTokenize(contactId),
        dto,
      );
      const normalized = normalizeContactPaymentMethod(raw);
      if (!normalized) {
        throw new Error('Stored payment method response was invalid');
      }
      return normalized;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<ContactPaymentMethod[]>(
        contactKeys.paymentMethods(contactId),
        (current = []) =>
          current.some((method) => method.id === created.id) ? current : [...current, created],
      );
      invalidateContactPaymentQueries(queryClient, contactId);
      toast.success('Payment method saved');
    },
  });
}

export function useRequestContactPaymentMethod(contactId: string) {
  return useApiMutation<ContactPaymentRequestResult, RequestContactPaymentMethodDto>({
    mutationFn: async (dto) =>
      apiPost<ContactPaymentRequestResult>(
        operationsClient,
        contactEndpoints.paymentMethodRequest(contactId),
        dto,
      ),
    onSuccess: (result) => {
      const notice = getContactPaymentRequestDeliveryNotice(result);
      const options = notice.description ? { description: notice.description } : undefined;
      if (notice.tone === 'success') {
        toast.success(notice.title, options);
      } else if (notice.tone === 'warning') {
        toast.warning(notice.title, options);
      } else {
        toast.error(notice.title, options);
      }
    },
  });
}

/**
 * The collected payment row, plus the receipt outcome both collect routes
 * report as `receiptDelivery`.
 */
type CollectedPaymentResult = Record<string, unknown> & {
  status?: unknown;
  paymentMethod?: unknown;
  receiptSent?: boolean;
  receiptDelivery?: CollectReceiptDelivery | null;
};

interface CollectContactPaymentOptions {
  /**
   * Suppress this hook's own generic result toast ("Payment collected" /
   * "Bank payment submitted" / "Payment is pending..."). Query invalidation
   * still runs either way. For a caller that shows its own more specific
   * copy for the same result (e.g. charging a newly-entered card/bank
   * account, which needs the exact dollar amount in the message) — set this
   * to `false` so the operator doesn't see two toasts stack for one charge.
   * Defaults to `true`, matching every caller before this option existed.
   */
  showSuccessToast?: boolean;
  /**
   * Suppress `useApiMutation`'s own generic error toast (the raw
   * `error.message`). For a caller that classifies the failure itself —
   * a decline vs. an ambiguous "we don't know if this went through" outcome
   * (network error, timeout, 5xx, 429, or a still-processing/mismatched
   * idempotency key) — and shows its own copy for each, so staff never see
   * two conflicting messages for one failed charge. Defaults to `true`,
   * matching every caller before this option existed.
   */
  showErrorToast?: boolean;
}

export function useCollectContactPayment(
  contactId: string,
  options: CollectContactPaymentOptions = {},
) {
  const queryClient = useQueryClient();
  const showSuccessToast = options.showSuccessToast ?? true;
  const showErrorToast = options.showErrorToast ?? true;

  return useApiMutation<CollectedPaymentResult, CollectContactPaymentDto>({
    showErrorToast,
    mutationFn: async (dto) => {
      // The route requires an `Idempotency-Key`, and the key has to SURVIVE a
      // lost response: a collection that committed on the server but never
      // reached the browser must REPLAY on the operator's next click, not run
      // the card again. Minting one per call could not do that — every retry
      // arrived as a brand-new charge. `billingV2Post` owns exactly this rule
      // for every other money-moving POST: one key per logical submission,
      // held while the outcome is unknown (transport, 5xx, 429, a 409 that is
      // not a key mismatch) and dropped the moment it is definitive, so a
      // deliberate second collection still gets a fresh key.
      //
      // The body carries no key: the controller reads the header and 400s on a
      // body key that disagrees with it, so the header is the only sensible
      // source. An explicit caller key still wins over the managed one.
      const { idempotencyKey, ...payload } = dto;
      const payment = await billingV2Post<
        CollectedPaymentResult,
        Omit<CollectContactPaymentDto, 'idempotencyKey'>
      >(
        contactEndpoints.collectPayment(contactId),
        payload,
        idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
      );
      // A decline is answered with the failed payment, not an error status.
      if (payment?.status === 'FAILED') {
        // That payment is saved, and the decline changes the card's health, so
        // refresh the contact's payments before reporting it; `onSuccess` never
        // runs for a thrown result.
        invalidateContactPaymentQueries(queryClient, contactId);
        throw new ApiError(
          422,
          'VALIDATION_ERROR',
          typeof payment.failureReason === 'string' && payment.failureReason.trim()
            ? payment.failureReason
            : 'Payment failed. Please check the payment method and try again.',
        );
      }
      return payment;
    },
    onSuccess: (payment) => {
      invalidateContactPaymentQueries(queryClient, contactId);
      if (!showSuccessToast) return;
      if (isAcceptedBankDebit(payment)) {
        const receipt = collectPaymentToast(payment?.receiptDelivery, 'Bank payment submitted');
        showReceiptToast({
          ...receipt,
          description:
            receipt.description ??
            'Bank payments usually clear in a few business days. It counts as paid while it clears; if the bank returns it, the balance goes back up.',
        });
      } else if (payment?.status === 'PENDING') {
        toast.info('Payment is pending. Check the Payments tab for its status.');
      } else {
        showReceiptToast(collectPaymentToast(payment?.receiptDelivery, 'Payment collected'));
      }
    },
  });
}

export function useRecordContactPayment(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<CollectedPaymentResult, RecordContactPaymentDto>({
    mutationFn: async (dto) =>
      apiPost<CollectedPaymentResult>(operationsClient, '/payments/collect', dto),
    onSuccess: (result) => {
      invalidateContactPaymentQueries(queryClient, contactId);
      showReceiptToast(collectPaymentToast(result?.receiptDelivery));
    },
  });
}

export function useRequestContactPaymentLink(contactId: string) {
  return useApiMutation<ContactPaymentLinkResult, CreateContactPaymentLinkDto>({
    mutationFn: async (dto) =>
      apiPost<ContactPaymentLinkResult>(
        operationsClient,
        contactEndpoints.requestPaymentLink(contactId),
        dto,
      ),
    onSuccess: () => {
      toast.success('Secure payment link created');
    },
  });
}

export function usePublicContactPaymentMethodRequest(token: string) {
  return useApiQuery<ContactPaymentMethodRequestDetails>({
    queryKey: ['public-contact-payment-method-request', token],
    queryFn: async () =>
      publicGet<ContactPaymentMethodRequestDetails>(
        publicContactPaymentMethodRequestEndpoints.detail(token),
      ),
    enabled: Boolean(token),
    showErrorToast: false,
  });
}

/**
 * The unauthenticated add-card page's setup intent. Narrower than the staff
 * one: the backend's public projection withholds `providerCustomerId`, and the
 * page must not send one back — the server resolves the contact's own Stripe
 * customer on completion.
 */
export function useCreatePublicContactPaymentMethodSetupIntent(token: string) {
  return useApiMutation<PublicContactPaymentMethodSetupIntent>({
    mutationFn: async () =>
      publicPost<PublicContactPaymentMethodSetupIntent>(
        publicContactPaymentMethodRequestEndpoints.setupIntent(token),
      ),
  });
}

/**
 * These two do NOT run the response through `normalizeContactPaymentMethod`.
 * That normalizer treats a missing `providerPaymentMethodId` as a malformed
 * payload and returns null — correct for the staff endpoints, wrong here,
 * because the public projection withholds the `pm_…` on purpose. It is the row
 * `id` that identifies a saved card everywhere in this app.
 */
function readPublicSavedPaymentMethod(raw: Record<string, unknown>): PublicSavedPaymentMethod {
  if (!raw.id) {
    throw new Error('Stored payment method response was invalid');
  }
  return raw as unknown as PublicSavedPaymentMethod;
}

export function useCompletePublicContactPaymentMethodRequest(token: string) {
  return useApiMutation<PublicSavedPaymentMethod, CompletePublicPaymentMethodRequest>({
    mutationFn: async (dto) =>
      readPublicSavedPaymentMethod(
        await publicPost<Record<string, unknown>>(
          publicContactPaymentMethodRequestEndpoints.complete(token),
          dto,
        ),
      ),
  });
}

/**
 * Public Payabli save-a-card completion: exchange the embedded component's
 * temporary token for a stored method via the public tokenize endpoint.
 */
export function useCompletePublicContactPaymentMethodTokenize(token: string) {
  return useApiMutation<PublicSavedPaymentMethod, CompletePublicTokenizeRequest>({
    mutationFn: async (dto) =>
      readPublicSavedPaymentMethod(
        await publicPost<Record<string, unknown>>(
          publicContactPaymentMethodRequestEndpoints.tokenize(token),
          dto,
        ),
      ),
  });
}
